import { Request } from '@/modules/Requests/domain';
import { DateUtility } from '@/utils';
import { Ref, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HourWidget, PriorityStatus, UserAvatar } from '..';
import { Status } from '../Status';
import { Typology } from '../Typology';
import { PerformAction, RequestDetailsModal } from './components';

interface RequestCardProps {
  data: Request;
  lastRowRef?: Ref<HTMLDivElement>;
}

export const RequestCard = ({ data, lastRowRef }: RequestCardProps) => {
  const { t, i18n } = useTranslation();

  const [openDetails, setOpenDetails] = useState(false);

  const openDetailsCallback = useCallback(() => setOpenDetails(true), []);
  const closeDetailsCallback = useCallback(() => setOpenDetails(false), []);

  return (
    <>
      <div
        ref={lastRowRef}
        onClick={openDetailsCallback}
        className="flex cursor-pointer flex-col divide-y divide-gray-100 rounded-[5px] bg-white shadow-lg"
      >
        <div className="flex flex-col gap-3.5 p-2.5">
          <div className="flex justify-between">
            <PriorityStatus value={data.priority} />
            <Typology type={data.typology} />
          </div>
          <div className="flex flex-col gap-2.5 text-sm text-gray">
            <p className="font-black">{data.reference}</p>
            <p className="font-normal">{data.title}</p>
          </div>
          <div className="flex items-center gap-1">
            <UserAvatar size="small" name={data.createdBy?.fullName} />
            <div className="flex text-xs font-normal italic text-gray-300">
              {`${t('request_created_at')} ${DateUtility.formatDate(data.createdAt, i18n.language)}`}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-1 p-2.5">
          <div className="flex items-center gap-1">
            <Status status={data.status} />
            <HourWidget
              analysisSeconds={data.analysisSeconds ?? 0}
              estimatedSeconds={data.estimatedSeconds ?? 0}
              usedSeconds={data.usedSeconds ?? 0}
              status={data.status}
            />
            <PerformAction status={data.status} />
          </div>
          <UserAvatar size="small" name={data.userResponsable?.fullName} />
        </div>
      </div>
      {openDetails && (
        <RequestDetailsModal
          isOpen={openDetails}
          onClose={closeDetailsCallback}
          requestId={data.id}
        />
      )}
    </>
  );
};
